import { skipToken } from "@reduxjs/toolkit/query";
import Loader from "components/ui/Loader";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery, useLogoutMutation } from "../../store/api/authApi";
import type { UserRole } from "../../types";
import Icon, { type IconName } from "../AppIcon";
import NotificationBell from "../notifications/NotificationBell";
import Button from "../ui/Button";

interface DashboardMenuItem {
	label: string;
	path: string;
	icon: IconName;
}

const TopNavigationBar: React.FC = () => {
	const navigate = useNavigate();
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const profileRef = useRef<HTMLDivElement | null>(null);
	// Only fetch profile if user has a token (skip query if no token to avoid 403 errors)
	const accessToken = tokenStorage.getAccessToken();
	const { data: user, isLoading } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);
	const [logout] = useLogoutMutation();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				profileRef.current &&
				!profileRef.current.contains(event.target as Node)
			) {
				setIsProfileOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			tokenStorage.clearTokens();
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
			tokenStorage.clearTokens();
			navigate("/login");
		}
	};

	// Show loading state or don't render profile section until user data is available
	if (isLoading || !user) {
		return (
			<header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 z-1000">
				{/* Logo Section */}
				<div className="flex items-center space-x-3">
					<button
						onClick={() => navigate("/")}
						className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
					>
						<img
							src="/assets/images/desktop_logo.png"
							alt="Dofarahub Logo"
							className="h-8 w-auto object-contain"
						/>
					</button>
				</div>
				<div className="flex items-center space-x-2 md:space-x-4">
					<NotificationBell />
					<Loader size="sm" />
				</div>
			</header>
		);
	}

	const userName = `${user.first_name} ${user.last_name}`;
	const userEmail = user.email;
	const userRole = user.role as UserRole;

	// Get role-specific dashboard menu items
	const dashboardMenuItems = useMemo((): DashboardMenuItem[] => {
		if (!userRole || userRole === "admin") return [];

		switch (userRole) {
			case "tenant":
				return [
					{
						label: "My Dashboard",
						path: "/tenant-dashboard",
						icon: "LayoutDashboard",
					},
					{
						label: "My Payments",
						path: "/tenant-payments",
						icon: "CreditCard",
					},
					{
						label: "Maintenance Requests",
						path: "/tenant-maintenance",
						icon: "Wrench",
					},
					{
						label: "Messages",
						path: "/tenant-messages",
						icon: "MessageSquare",
					},
				];
			case "technician":
				return [
					{
						label: "My Dashboard",
						path: "/technician-dashboard",
						icon: "LayoutDashboard",
					},
					{
						label: "Assigned Tasks",
						path: "/technician-tasks",
						icon: "CheckSquare",
					},
					{
						label: "Messages",
						path: "/technician-messages",
						icon: "MessageSquare",
					},
				];
			case "agent":
				return [
					{
						label: "My Dashboard",
						path: "/agent-dashboard",
						icon: "LayoutDashboard",
					},
					{
						label: "Sales Management",
						path: "/sales-management",
						icon: "TrendingUp",
					},
					{
						label: "Viewing Management",
						path: "/property-viewing-management",
						icon: "Calendar",
					},
					{ label: "Messages", path: "/agent-messages", icon: "MessageSquare" },
				];
			default:
				return [];
		}
	}, [userRole]);

	return (
		<header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 z-1000">
			{/* Logo Section */}
			<div className="flex items-center space-x-3">
				<button
					onClick={() => navigate("/")}
					className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
				>
					<img
						src="/assets/images/desktop_logo.png"
						alt="Dofarahub Logo"
						className="h-8 w-auto object-contain"
					/>
				</button>
				<button
					onClick={() => navigate("/")}
					className="flex items-center space-x-2 hover:opacity-80 smooth-transition"
				>
					<Icon name="ArrowLeft" size={10} className="text-foreground" />
					<span className="text-sm font-medium text-foreground">
						Back to Home
					</span>
				</button>
			</div>

			{/* Right Section: Notifications + Profile */}
			<div className="flex items-center space-x-2 md:space-x-4">
				{/* Notifications */}
				<NotificationBell />

				{/* User Profile Dropdown */}
				<div className="relative" ref={profileRef}>
					<Button
						variant="ghost"
						onClick={() => setIsProfileOpen(!isProfileOpen)}
						className="flex items-center space-x-2 px-2 md:px-3"
					>
						<div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
							{user?.avatar ? (
								<img
									src={user.avatar}
									alt={userName}
									className="w-full h-full rounded-full object-cover"
								/>
							) : (
								<Icon name="User" size={16} />
							)}
						</div>
						<span className="hidden md:inline text-sm font-medium text-foreground">
							{userName}
						</span>
						<Icon
							name="ChevronDown"
							size={16}
							className={`smooth-transition ${
								isProfileOpen ? "rotate-180" : ""
							}`}
						/>
					</Button>

					{isProfileOpen && (
						<div className="absolute right-0 top-12 w-56 bg-popover border border-border rounded-lg modal-shadow z-[9998] animate-scale-in">
							<div className="p-4 border-b border-border">
								<p className="font-medium text-popover-foreground">
									{userName}
								</p>
								<p className="text-sm text-muted-foreground">{userEmail}</p>
								<span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full capitalize">
									{user.role}
								</span>
							</div>

							<div className="py-2">
								{/* Dashboard Menu Items (for non-admin users) */}
								{dashboardMenuItems.length > 0 && (
									<>
										{dashboardMenuItems.map((item) => (
											<button
												key={item.path}
												onClick={() => {
													setIsProfileOpen(false);
													navigate(item.path);
												}}
												className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
											>
												<Icon name={item.icon} size={16} />
												<span>{item.label}</span>
											</button>
										))}
										<div className="border-t border-border my-2" />
									</>
								)}
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/profile");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="User" size={16} />
									<span>Profile Settings</span>
								</button>
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/settings");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="Settings" size={16} />
									<span>Account Settings</span>
								</button>
								<button
									onClick={() => {
										setIsProfileOpen(false);
										navigate("/help");
									}}
									className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted smooth-transition flex items-center space-x-2"
								>
									<Icon name="HelpCircle" size={16} />
									<span>Help & Support</span>
								</button>
							</div>

							<div className="border-t border-border py-2">
								<button
									onClick={handleLogout}
									className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 smooth-transition flex items-center space-x-2"
								>
									<Icon name="LogOut" size={16} />
									<span>Sign Out</span>
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</header>
	);
};

export default TopNavigationBar;
